// DiagnosticLanding — landing screen.
//
// Editorial-feel layout (no boxed three-card row): a strong headline with a
// hand-drawn underline accent on the right, a tilted "paper" preview of a
// real diagnostic on the right, a floating score badge overlapping it, and
// understated metadata at the bottom. Two outbound actions:
//   - onStart()       → start the intake
//   - onShowExample() → preview the document view
//   - onExit()        → leave /diagnostic

import React from 'react';
import { FaArrowRight, FaArrowLeft, FaRegFileAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

export default function DiagnosticLanding({ onStart, onShowExample, onExit }) {
  const { t } = useTranslation();
  return (
    <section className="diag-landing">
      {/* Soft animated blobs for depth — no real meaning, just texture. */}
      <span className="diag-blob diag-blob-a" aria-hidden="true" />
      <span className="diag-blob diag-blob-b" aria-hidden="true" />

      <header className="diag-landing-top">
        <button
          type="button"
          className="diag-back diag-back-flat"
          onClick={onExit}
          aria-label={t('diagia.landing.quit', 'Quitter')}
        >
          <FaArrowLeft />
        </button>
        <span className="diag-landing-mark">{t('diagia.landing.mark', 'Diagnostic IA · Outil de cadrage')}</span>
      </header>

      <div className="diag-landing-stage">
        <div className="diag-landing-copy">
          <h1 className="diag-landing-h1">
            {t('diagia.home.h1_line1_pre', "D'un")} <em>{t('diagia.home.h1_line1_em', 'irritant')}</em> {t('diagia.home.h1_line1_post', 'ou un besoin métier')}
            <br />
            {t('diagia.home.h1_line2_pre', 'à un')} <span className="diag-h1-accent">
              {t('diagia.home.h1_line2_accent', "cas d'usage IA cadré")}
              {/* Sketchy hand-drawn underline */}
              <svg
                className="diag-h1-squiggle"
                viewBox="0 0 320 18"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M4 12 C 60 3, 120 17, 180 8 S 300 14, 316 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <br />
            {t('diagia.home.h1_line3', 'en moins de 3 minutes.')}
          </h1>

          <p className="diag-landing-lede">
            {t('diagia.landing.lede', "Une conversation guidée transforme un irritant du terrain en un dossier de cadrage prêt à présenter — enjeu, cas d'usage, scoring, données, indicateurs.")}
          </p>

          <div className="diag-landing-ctas">
            <button
              type="button"
              className="diag-cta diag-cta-primary"
              onClick={onStart}
            >
              {t('diagia.landing.cta_start', 'Lancer le diagnostic')}
              <FaArrowRight />
            </button>
            <button
              type="button"
              className="diag-cta diag-cta-link"
              onClick={onShowExample}
            >
              <FaRegFileAlt />
              {t('diagia.home.cta_example', 'Voir un exemple')}
            </button>
          </div>

          <p className="diag-landing-meta">
            <span><strong>5 {t('diagia.landing.questions', 'questions')}</strong> max</span>
            <span className="diag-dot" aria-hidden="true">·</span>
            <span><strong>3 min</strong> {t('diagia.landing.avg', 'en moyenne')}</span>
            <span className="diag-dot" aria-hidden="true">·</span>
            <span>Markdown &amp; PDF</span>
          </p>
        </div>

        <div className="diag-landing-art" aria-hidden="true">
          {/* The tilted "back" sheet, half-hidden, suggesting a stack */}
          <div className="diag-sheet diag-sheet-back" />

          {/* Foreground sheet — a real-looking snippet of the diagnostic */}
          <div className="diag-sheet diag-sheet-front">
            <div className="diag-sheet-eyebrow">{t('diagia.diag.eyebrow', 'Diagnostic IA')}</div>
            <h3 className="diag-sheet-title">{t('diagia.home.sheet_title', 'Registre géolocalisé des agriculteurs')}</h3>
            <p className="diag-sheet-sub">
              {t('diagia.home.sheet_sub', 'Cas prioritaire à cadrer en MVP.')}
            </p>

            <div className="diag-sheet-row">
              <span>{t('diagia.home.sheet_impact', 'Impact métier')}</span>
              <span className="diag-sheet-bar">
                <span style={{ width: '90%' }} />
              </span>
              <strong>9/10</strong>
            </div>
            <div className="diag-sheet-row">
              <span>{t('diagia.home.sheet_data', 'Faisabilité data')}</span>
              <span className="diag-sheet-bar">
                <span style={{ width: '70%' }} />
              </span>
              <strong>7/10</strong>
            </div>
            <div className="diag-sheet-row">
              <span>{t('diagia.home.sheet_adoption', 'Adoption métier')}</span>
              <span className="diag-sheet-bar">
                <span style={{ width: '80%' }} />
              </span>
              <strong>8/10</strong>
            </div>
            <div className="diag-sheet-row">
              <span>{t('diagia.home.sheet_roi', 'Potentiel ROI')}</span>
              <span className="diag-sheet-bar">
                <span style={{ width: '80%' }} />
              </span>
              <strong>8/10</strong>
            </div>

            <div className="diag-sheet-foot">
              <span>4 / 6 {t('diagia.home.sheet_criteria', 'critères')}</span>
              <span className="diag-sheet-divider" />
              <span>+ 2 {t('diagia.home.sheet_indicators', 'indicateurs')}</span>
            </div>
          </div>

          {/* Floating score chip overlapping the sheet */}
          <div className="diag-score-chip">
            <span className="diag-score-num">75</span>
            <span className="diag-score-den">/100</span>
            <span className="diag-score-label">{t('diagia.home.score_label', 'Score global')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
